import { clamp, ramp } from "../shared";

const hash = (x, y) => { const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453; return s - Math.floor(s); };
function noise(x, y) {
  const xi = Math.floor(x), yi = Math.floor(y), xf = x - xi, yf = y - yi, u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf);
  const a = hash(xi, yi), b = hash(xi + 1, yi), c = hash(xi, yi + 1), d = hash(xi + 1, yi + 1);
  return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
}

class Fractal {
  constructor(ctx) { this.ctx = ctx; this.writesColors = true; this.resetView(); }
  resetView() { this.cx = this.ctx.params.type === "mandelbrot" ? -0.6 : 0; this.cy = 0; this.zoom = 1; this.dirty = true; }
  resize() { this.dirty = true; }
  pointer(e) {
    if (e.type !== "down") return;
    const s = 1.6 / this.zoom; this.cx += e.u * s; this.cy += e.v * s;
    this.zoom = clamp(this.zoom * (e.shift ? 0.5 : 2), 0.25, 1e6); this.dirty = true;
  }
  value(u, v, t) {
    const P = this.ctx.params, it = P.iter, s = 1.6 / this.zoom;
    switch (P.type) {
      case "mandelbrot": case "julia": {
        let zr, zi, cr, ci;
        if (P.type === "mandelbrot") { zr = 0; zi = 0; cr = this.cx + u * s; ci = this.cy + v * s; }
        else { zr = this.cx + u * s; zi = this.cy + v * s; const a = P.animate ? t * 0.25 : 0.6; cr = 0.7885 * Math.cos(a); ci = 0.7885 * Math.sin(a); }
        let k = 0; for (; k < it && zr * zr + zi * zi < 16; k++) { const nz = zr * zr - zi * zi + cr; zi = 2 * zr * zi + ci; zr = nz; }
        if (k >= it) return 1;
        const mu = k + 1 - Math.log(Math.log(Math.sqrt(zr * zr + zi * zi))) / Math.LN2;
        return Math.sqrt(clamp(mu / it, 0, 1));
      }
      case "carpet": {
        const L = clamp(Math.round(it / 12), 1, 6); let x = (this.cx + u * s / 1.6) * 0.5 + 0.5, y = (this.cy + v * s / 1.6) * 0.5 + 0.5;
        if (x < 0 || x > 1 || y < 0 || y > 1) return 0;
        for (let k = 1; k <= L; k++) { x *= 3; y *= 3; if (Math.floor(x) % 3 === 1 && Math.floor(y) % 3 === 1) return (k - 1) / L; }
        return 1;
      }
      case "terrain": {
        const oct = clamp(Math.round(it / 12), 1, 8), sh = P.animate ? t * 0.15 : 0; let a = 0.5, f = 1.2 / this.zoom, sum = 0, n = 0;
        for (let k = 0; k < oct; k++) { sum += a * noise((u + this.cx) * f + sh + k * 17, (v + this.cy) * f + k * 31); n += a; a *= 0.5; f *= 2; }
        return Math.pow(sum / n, 1.6) * 1.4;
      }
      case "saddle": { const a = P.animate ? t * 0.4 : 0, x = (u * Math.cos(a) - v * Math.sin(a)) / this.zoom, y = (u * Math.sin(a) + v * Math.cos(a)) / this.zoom; return 0.5 + 0.5 * (x * x - y * y); }
      case "sinc": { const r = Math.hypot(u, v) * 14 / this.zoom; return 0.35 + 0.65 * Math.sin(r - (P.animate ? t * 3 : 0)) / (r * 0.3 + 1); }
      default: { const k = (3 * Math.PI) / this.zoom, ph = P.animate ? t : 0; return 0.5 + 0.5 * Math.sin(u * k + ph) * Math.cos(v * k + ph * 0.7); }
    }
  }
  step(dt, t) {
    const P = this.ctx.params; if (!P.animate && !this.dirty) return;
    if (P.animate && (P.type === "mandelbrot" || P.type === "carpet") && !this.dirty) return;
    const { N, px, half, target, colors } = this.ctx;
    for (let r = 0; r < N; r++) for (let c = 0; c < N; c++) {
      const i = r * N + c; let v = clamp(this.value(px[c] / half, px[r] / half, t), 0, 1);
      if (P.invert) v = 1 - v;
      target[i] = (-1 + 2 * v) * P.relief * this.ctx.g.force; ramp(colors, i, v);
    }
    this.dirty = false;
  }
  onParams(key) { if (key === "type") this.resetView(); this.dirty = true; }
  action() { this.ctx.pushHistory(); this.resetView(); }
  snapshot() { return { cx: this.cx, cy: this.cy, zoom: this.zoom }; }
  restore(s) { Object.assign(this, s); this.dirty = true; }
  clear() { this.resetView(); }
  telemetry() {
    return [["Type", this.ctx.params.type.toUpperCase()], ["Zoom", `${this.zoom >= 1000 ? this.zoom.toExponential(1) : this.zoom}×`], ["Center", `${this.cx.toFixed(4)}, ${this.cy.toFixed(4)}`], ["Iterations", this.ctx.params.iter]];
  }
}

const fractal = {
  id: "fractal-lab", name: "Fractal Lab", icon: "Snowflake", rest: 0,
  desc: "Escape-time fractals, recursive geometry and analytic surfaces rendered as pin height. Tap the board to zoom ×2 into that point (Shift-tap zooms out); iterations control the recursion depth.",
  math: "z_{n+1} = z_n² + c   ·   escape when |z| > 4",
  params: [
    { key: "type", label: "Pattern", type: "select", options: [["mandelbrot", "Mandelbrot"], ["julia", "Julia"], ["carpet", "Sierpinski"], ["terrain", "fBm terrain"], ["saddle", "Saddle z=x²−y²"], ["sinc", "Sinc ripple"], ["egg", "Egg crate"]], def: "mandelbrot" },
    { key: "iter", label: "Iterations / depth", type: "range", min: 8, max: 200, step: 1, def: 48 },
    { key: "relief", label: "Relief", type: "range", min: 0.1, max: 1, step: 0.01, def: 0.85 },
    { key: "animate", label: "Animate (Julia c, terrain, surfaces)", type: "toggle", def: true },
    { key: "invert", label: "Invert heights", type: "toggle", def: false },
  ],
  actions: [{ id: "resetView", label: "Reset view" }],
  create: (ctx) => new Fractal(ctx),
};
export default fractal;
